/**
 * Mission Control panel types. Mirrors /v1/ops/control (migration 028).
 *
 * Nothing here is synthesised on the client: an agent that has never run
 * carries status "never_run", and an edge that has never carried work carries
 * observed 0, so the panel can draw the difference between what the fleet is
 * wired to do and what it has actually done.
 */

import type { OpsColumn, OpsStream } from "./board";

export type AgentKind = "chief" | "planner" | "worker" | "reviewer" | "watchdog";
export type AgentStatus = "never_run" | "idle" | "running" | "blocked" | "error";

export interface OpsAgent {
  id: string;
  name: string;
  role: string;
  kind: AgentKind;
  streamId: string | null;
  model: string;
  effort: string;
  cadence: string | null;
  colour: string;
  position: number;
  instructions: string;
  instructionsUpdatedAt: string | null;
  instructionsUpdatedBy: string | null;
  enabled: boolean;
  status: AgentStatus;
  statusDetail: string | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
}

export interface OpsAgentRun {
  id: number;
  agentId: string;
  startedAt: string;
  endedAt: string | null;
  status: string;
  summary: string | null;
  cardsCreated: number;
  cardsMoved: number;
  cardsClosed: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  error: string | null;
}

export interface OpsAgentEdge {
  fromAgent: string;
  toAgent: string;
  kind: string;
  label: string | null;
  /** Real traffic in the last 30 days. 0 means designed but never used. */
  observed: number;
  lastAt: string | null;
}

export interface AgentWorkload {
  agentId: string;
  name: string;
  colour: string;
  runs: number;
  cardsCreated: number;
  cardsMoved: number;
  cardsClosed: number;
  costUsd: number;
  minutes: number;
  actions: number;
}

export interface OpsChatMessage {
  id: number;
  role: "jared" | "agent";
  agentId: string | null;
  text: string;
  answered: boolean;
  createdAt: string;
}

export interface OpsFocus {
  headline: string;
  body: string;
  needsJared: { ref?: string; title: string; why?: string }[];
  updatedBy: string | null;
  updatedAt: string;
}

export interface ControlPanelResponse {
  agents: OpsAgent[];
  edges: OpsAgentEdge[];
  workload: AgentWorkload[];
  activity: { day: string; runs: number; actions: number }[];
  runs: OpsAgentRun[];
  /** Null until the chief of staff has run. The page must handle that. */
  focus: OpsFocus | null;
  chat: OpsChatMessage[];
  unanswered: number;
  board: {
    counts: Record<string, number>;
    paused: boolean;
    streams: OpsStream[];
    needsJared: { ref: string; title: string; priority: number; streamId: string }[];
    inFlight: {
      ref: string; title: string; col: OpsColumn; streamId: string;
      claimedBy: string | null; reviewLaps: number; prUrl: string | null;
    }[];
  };
}

export const AGENT_STATUS_LABEL: Record<AgentStatus, string> = {
  never_run: "Never run",
  idle: "Idle",
  running: "Running",
  blocked: "Blocked",
  error: "Error"
};

export const AGENT_KIND_LABEL: Record<AgentKind, string> = {
  chief: "Chief",
  planner: "Planner",
  worker: "Worker",
  reviewer: "Reviewer",
  watchdog: "Watchdog"
};

/**
 * Sequential blue ramp, light to dark, for magnitude. Validated against the
 * Orbit dark surface. Magnitude charts use this rather than the agents' own
 * identity hues, because there are twelve agents and no categorical palette
 * survives twelve slots. Identity is carried by the dot beside each label.
 */
// Ordered light -> dark for a LIGHT surface. On this dark surface it is read in
// reverse: more magnitude means LIGHTER, or the busiest agent's bar is the one
// that disappears into the background while the idle ones glow.
export const SEQ_BLUE = [
  "#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf", "#184f95", "#0d366b"
];

/** Magnitude -> colour, brightest at the top, for the dark chart surface. */
export function seqStep(value: number, max: number): string {
  if (max <= 0) return SEQ_BLUE[4];
  const t = Math.min(1, Math.max(0, value / max));
  // 4 (mid) at zero, 0 (lightest) at max. Never the darkest two steps: below
  // roughly step 4 the fill stops clearing 2:1 against #11112a.
  const i = Math.round(4 - t * 4);
  return SEQ_BLUE[Math.max(0, Math.min(SEQ_BLUE.length - 1, i))];
}
